import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useParams } from "react-router-dom";
import type { AdminEventHubSummary, FeaturedEventInfo, ScheduledEvent } from "@tih/shared";
import {
  ApiError,
  adminFetchEvent,
  adminFetchEventHubSummary,
  adminFetchFeaturedEvent,
} from "../api/client";
import { eventDisplayTitle, rowPhase, type EventRowPhase } from "../lib/event-admin-utils";

type EventHubContextValue = {
  eventId: number;
  event: ScheduledEvent | null;
  summary: AdminEventHubSummary | null;
  featuredId: number | null;
  phase: EventRowPhase | null;
  displayTitle: string;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  refreshSummary: () => Promise<void>;
  setEvent: (event: ScheduledEvent) => void;
};

const EventHubContext = createContext<EventHubContextValue | null>(null);

export function EventHubProvider({ children }: { children: ReactNode }) {
  const { id } = useParams();
  const eventId = Number(id);

  const [event, setEvent] = useState<ScheduledEvent | null>(null);
  const [summary, setSummary] = useState<AdminEventHubSummary | null>(null);
  const [featuredId, setFeaturedId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshSummary = useCallback(async () => {
    if (!Number.isFinite(eventId)) return;
    const data = await adminFetchEventHubSummary(eventId);
    setSummary(data);
  }, [eventId]);

  const refresh = useCallback(async () => {
    if (!Number.isFinite(eventId)) {
      setError("Invalid event id");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [ev, sum, featured] = await Promise.all([
        adminFetchEvent(eventId),
        adminFetchEventHubSummary(eventId),
        adminFetchFeaturedEvent(),
      ]);
      setEvent(ev);
      setSummary(sum);
      setFeaturedId((featured as FeaturedEventInfo | null)?.id ?? null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not load event");
      setEvent(null);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const phase = event ? rowPhase(event) : null;
  const displayTitle = event ? eventDisplayTitle(event) : "Event";

  const value = useMemo<EventHubContextValue>(
    () => ({
      eventId,
      event,
      summary,
      featuredId,
      phase,
      displayTitle,
      loading,
      error,
      refresh,
      refreshSummary,
      setEvent,
    }),
    [
      eventId,
      event,
      summary,
      featuredId,
      phase,
      displayTitle,
      loading,
      error,
      refresh,
      refreshSummary,
    ],
  );

  return <EventHubContext.Provider value={value}>{children}</EventHubContext.Provider>;
}

export function useEventHub(): EventHubContextValue {
  const ctx = useContext(EventHubContext);
  if (!ctx) throw new Error("useEventHub must be used within EventHubProvider");
  return ctx;
}
